<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Species</h1>
				<p class="text-muted mb-0">
					Species is shared reference data: one Gleipnir-curated catalog, and
					your district's own local set adopted from it. Your existing species
					can be linked to the catalog by name, and a species the catalog does
					not carry is suggested to Gleipnir rather than added here.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- The seeding: the first thing a district does with the catalog, and a
		     report rather than a guess — a name the catalog does not carry is
		     listed below and stays legal. -->
		<div class="card mb-4">
			<div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
				<div>
					<h2 class="h5 mb-1">Link your species to the catalog</h2>
					<p class="text-muted mb-0 small">
						Links every species your district already names to the catalog entry
						carrying the same name — comparison ignores case and surrounding
						spaces.
					</p>
				</div>
				<ButtonLoading
					text="Link by name"
					icon="bi bi-link-45deg"
					variant="primary"
					:loading="seeding"
					@click="seed"
				/>
			</div>
		</div>

		<div class="card mb-4">
			<div class="card-body">
				<div class="d-flex justify-content-between align-items-center mb-3">
					<h2 class="h5 mb-0">Shared catalog</h2>
					<span class="text-muted small">
						{{ adoptedCount }} of {{ catalog.length }} adopted
					</span>
				</div>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="catalog.length === 0"
					class="text-center py-4 text-muted"
				>
					The shared catalog is empty. Gleipnir curates it.
				</div>

				<div v-else class="row g-3">
					<div
						v-for="entry in catalog"
						:key="entry.species_id"
						class="col-12 col-md-6 col-lg-4"
					>
						<div
							class="card h-100 border-0 shadow-sm"
							:class="{ 'bg-light': entry.retired }"
						>
							<div class="card-body d-flex flex-column">
								<div class="d-flex justify-content-between align-items-start">
									<h3
										class="h6 mb-1"
										:class="{ 'text-muted': entry.retired }"
									>
										{{ entry.name }}
									</h3>
									<span
										v-if="entry.retired"
										class="badge bg-secondary ms-2"
									>
										Retired
									</span>
								</div>
								<p class="text-muted small mb-3">
									<span v-if="entry.acronym">{{ entry.acronym }}</span>
									<span v-if="entry.acronym && entry.full_name"> · </span>
									<span v-if="entry.full_name">{{ entry.full_name }}</span>
									<span v-if="!entry.acronym && !entry.full_name">
										{{ entry.taxonomic_rank || entry.species_id }}
									</span>
								</p>
								<div class="mt-auto">
									<span
										v-if="entry.local_species_id !== null"
										class="badge bg-success"
									>
										In your set
									</span>
									<ButtonLoading
										v-else
										:text="adoptingId === entry.species_id ? 'Adding…' : 'Add to district'"
										icon="bi bi-plus-lg"
										variant="outline-primary"
										size="sm"
										:loading="adoptingId === entry.species_id"
										@click="adopt(entry)"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="card">
			<div class="card-body">
				<h2 class="h5 mb-1">Not in the catalog</h2>
				<p class="text-muted small">
					Species your district names that no catalog entry matches. Gleipnir owns
					the catalog, so suggest an addition rather than adding one here.
				</p>

				<div v-if="unlinked.length === 0" class="text-muted small mb-3">
					Every species your district names is in the catalog.
				</div>
				<ul v-else class="list-group mb-3">
					<li
						v-for="name in unlinked"
						:key="name"
						class="list-group-item d-flex justify-content-between align-items-center"
					>
						<span>{{ name }}</span>
						<ButtonLoading
							text="Suggest"
							icon="bi bi-send"
							variant="outline-secondary"
							size="sm"
							@click="prefillSuggestion(name)"
						/>
					</li>
				</ul>

				<form class="row g-2 align-items-end" @submit.prevent="suggest">
					<div class="col-12 col-md-5">
						<label class="form-label" for="suggestionName">Species name</label>
						<input
							id="suggestionName"
							v-model="suggestionName"
							type="text"
							class="form-control"
							placeholder="e.g. Culex tarsalis"
						/>
					</div>
					<div class="col-12 col-md-5">
						<label class="form-label" for="suggestionNote">
							Note (optional)
						</label>
						<input
							id="suggestionNote"
							v-model="suggestionNote"
							type="text"
							class="form-control"
							placeholder="why it should be in the catalog"
						/>
					</div>
					<div class="col-12 col-md-2">
						<ButtonLoading
							type="submit"
							text="Suggest"
							icon="bi bi-send"
							variant="primary"
							:loading="suggesting"
							:disabled="suggestionName.trim() === ''"
						/>
					</div>
				</form>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	SpeciesAdoptWrite,
	SpeciesCatalogEntry,
	SpeciesCatalogResponse,
	SpeciesCatalogSuggestionWrite,
} from "@/type/api";

// The Configuration toggle list (§4.1, §6.7, D25/D26): the shared Gleipnir
// catalog with an adopt control per entry, the district's species no catalog
// entry matches, and the two acts that change either — adopting an entry (the
// same act the workbench's escape hatch takes, D25) and suggesting an addition
// to Gleipnir, which lands as a Communication (D26).
const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const catalog = ref<SpeciesCatalogEntry[]>([]);
const unlinked = ref<string[]>([]);
const loading = ref(false);
const error = ref("");
const seeding = ref(false);
const adoptingId = ref<string | null>(null);

const suggestionName = ref("");
const suggestionNote = ref("");
const suggesting = ref(false);

const adoptedCount = computed(
	() => catalog.value.filter((entry) => entry.local_species_id !== null).length,
);

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const data = await apiClient.JSONGet<SpeciesCatalogResponse>(
			"/api/species-catalog",
		);
		catalog.value = data.catalog ?? [];
		unlinked.value = data.unlinked ?? [];
	} catch (e: any) {
		error.value = e?.message || "Failed to load the species catalog.";
		showNotification("Failed to load species", error.value);
	} finally {
		loading.value = false;
	}
}

async function seed() {
	seeding.value = true;
	try {
		const result = await apiClient.JSONPost<{
			linked: number;
			unlinked: string[];
		}>("/api/species-catalog/seed", {});
		await load();
		showNotification(
			"Species link complete",
			`Linked ${result.linked} species to the catalog. ${
				result.unlinked.length
			} remain unmatched.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to link species",
			e?.message || "The seeding did not run.",
		);
	} finally {
		seeding.value = false;
	}
}

async function adopt(entry: SpeciesCatalogEntry) {
	adoptingId.value = entry.species_id;
	try {
		const body: SpeciesAdoptWrite = { catalogSpeciesId: entry.species_id };
		const adopted = await apiClient.JSONPost<SpeciesCatalogEntry>(
			"/api/species",
			body,
		);
		await load();
		showNotification(
			"Species added",
			`${adopted.name} is now in your district's set.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to add species",
			e?.message || `${entry.name} was not added.`,
		);
	} finally {
		adoptingId.value = null;
	}
}

function prefillSuggestion(name: string) {
	suggestionName.value = name;
}

async function suggest() {
	const name = suggestionName.value.trim();
	if (name === "") return;
	suggesting.value = true;
	try {
		const body: SpeciesCatalogSuggestionWrite = {
			name,
			note: suggestionNote.value.trim() || null,
		};
		await apiClient.JSONPost<{ communication_id: number }>(
			"/api/species-catalog/suggestion",
			body,
		);
		suggestionName.value = "";
		suggestionNote.value = "";
		showNotification(
			"Suggestion sent",
			`${name} has been suggested to Gleipnir.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to send suggestion",
			e?.message || "The suggestion was not recorded.",
		);
	} finally {
		suggesting.value = false;
	}
}

onMounted(load);
</script>
